import { Feature, Point } from "geojson";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { Layer, Source } from "react-map-gl";
import { useDrawMapStore, useMapStore } from "../..";
import useMapIcon from "../../hooks/useMapIcon";
import useNavigateToLeaseComp from "../../hooks/useNavigateToLeaseComp";
import useSubscribeMapMouseEvent from "../../hooks/useSubscribeMapMouseEvent";
import useRadiusLeaseCompsOnMap from "../../hooks/useRadiusLeaseCompsOnMap";
import useVisibleCompaniesLeaseCompsOnMap from "../../hooks/useVisibleCompaniesLeaseCompsOnMap";
import useVisibleLeaseCompsOnMaps from "../../hooks/useVisibleLeaseCompsOnMap";
import ILayerProps from "../../interfaces/IlayerProps";
import { minZoom } from "../mapConfig";

export const leaseCompMarkerLayerId = "leaseCompMarkerLayer";

function LeaseCompsMapLayer({ visible }: ILayerProps) {
    const navigateToLeaseComp = useNavigateToLeaseComp();
    const mapStore = useMapStore();
    const drawMapStore = useDrawMapStore();
    const leaseComps = useVisibleLeaseCompsOnMaps();
    const companiesLeaseComps = useVisibleCompaniesLeaseCompsOnMap();
    const radiusLeaseComps = useRadiusLeaseCompsOnMap();

    useMapIcon({ name: "lease-comp-marker", url: "./lease-comp.png" });
    useSubscribeMapMouseEvent(() =>
        mapStore.onMouseClick("leasecomps", feature => {
            if (feature?.layer?.id === leaseCompMarkerLayerId && !drawMapStore.isActiveDrawing()) {
                navigateToLeaseComp(Number(feature.id));
            }
        })
    );

    const leaseCompMarkersGeoJson: any = useMemo(() => {
        const features = new Map<number, Feature<Point>>();

        if (visible) {
            for (const leaseComp of leaseComps) {
                features.set(leaseComp.id, {
                    type: "Feature",
                    id: leaseComp.id,
                    geometry: {
                        type: "Point",
                        coordinates: [leaseComp.centerPoint.longitude, leaseComp.centerPoint.latitude],
                    },
                    properties: {
                        icon: "lease-comp-marker",
                        minZoom: minZoom.leaseComps,
                    },
                });
            }
        }

        for (const companiesLeaseComp of companiesLeaseComps) {
            features.set(companiesLeaseComp.id, {
                type: "Feature",
                id: companiesLeaseComp.id,
                geometry: {
                    type: "Point",
                    coordinates: [companiesLeaseComp.centerPoint.longitude, companiesLeaseComp.centerPoint.latitude],
                },
                properties: {
                    icon: companiesLeaseComp.icon,
                    minZoom: minZoom.locations,
                },
            });
        }

        for (const radiusLeaseComp of radiusLeaseComps) {
            features.set(radiusLeaseComp.id, {
                type: "Feature",
                id: radiusLeaseComp.id,
                geometry: {
                    type: "Point",
                    coordinates: [radiusLeaseComp.centerPoint.longitude, radiusLeaseComp.centerPoint.latitude],
                },
                properties: {
                    icon: radiusLeaseComp.icon,
                    minZoom: minZoom.locations,
                },
            });
        }

        return { type: "FeatureCollection", features: Array.from(features.values()) };
    }, [leaseComps, companiesLeaseComps, radiusLeaseComps, visible]);

    if (!mapStore.isStyleLoaded)
        return null;

    return (
        <Source id="leaseCompMarkerSource" type="geojson" data={leaseCompMarkersGeoJson}>
            <Layer
                id={leaseCompMarkerLayerId}
                type="symbol"
                filter={["<=", ["get", "minZoom"], ["zoom"]]}
                layout={{
                    "icon-allow-overlap": true,
                    "symbol-z-order": "source",
                    "icon-image": ["get", "icon"],
                }}
            />
        </Source>
    );
}

export default observer(LeaseCompsMapLayer);